import {
  ExperiencePerformanceTypes,
  ExperienceTypes,
  UFOExperience,
} from '@atlassian/ufo';
import { DatabaseChangeType } from '@databases/types/redux';

export enum Metrics {
  STANDALONE_DETAIL_LOAD = 'standalone-detail-load',
  STANDALONE_ENTRY_LOAD = 'standalone-entry-load',

  EMBED_REFERENCE_LOAD = 'embed-reference-load',
  EMBED_VALUE_LOAD = 'embed-value-load',
  EMBED_ENTRY_LOAD = 'embed-entry-load',
  EMBED_DETAIL_LOAD = 'embed-detail-load',

  MEAN_TEXT_VALUE_TYPING = 'mean-text-value-typing',
  MEAN_DATABASE_NAME_TYPING = 'mean-database-name-typing',
  MEAN_FIELD_NAME_TYPING = 'mean-field-name-typing',
}

type InvalidMetricChangeTypes =
  | DatabaseChangeType.SET_NAME
  | DatabaseChangeType.RESET_NAME
  | DatabaseChangeType.SET_SPACE
  | DatabaseChangeType.SET_IS_GLOBAL
  | DatabaseChangeType.UPDATE_FIELD
  | DatabaseChangeType.RESET_FIELD
  | DatabaseChangeType.RESET_FIELD_NAME
  | DatabaseChangeType.UNDELETE_FIELD
  | DatabaseChangeType.RESET_VALUE
  | DatabaseChangeType.UNDELETE_ENTRY
  | DatabaseChangeType.SET_MAIN_FIELD_ID
  | DatabaseChangeType.SET_FIELD_SETTINGS;

export const CollabChangeMetrics: Record<
  Exclude<DatabaseChangeType, InvalidMetricChangeTypes>,
  string
> = {
  [DatabaseChangeType.ADD_ENTRY]: 'database-change-add-entry',
  [DatabaseChangeType.ADD_FIELD]: 'database-change-add-field',
  [DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT]:
    'database-change-set-field-settings-number-format',
  [DatabaseChangeType.SET_FIELD_SETTINGS_MEDIA_FORMAT]:
    'database-change-set-field-settings-media-format',
  [DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD]:
    'database-change-set-field-settings-minimum-or-maximum-fraction-digits-field',
  [DatabaseChangeType.SET_FIELD_SETTINGS_SEPARATOR_FIELD]:
    'database-change-set-field-settings-separator-field',
  [DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD]:
    'database-change-set-field-settings-prefix-or-suffix-field',
  [DatabaseChangeType.SET_IS_CREATABLE]: 'database-change-set-is-creatable',
  [DatabaseChangeType.SET_IS_MULTI]: 'database-change-set-is-multi',
  [DatabaseChangeType.SET_ALLOW_EDIT]: 'database-change-set-allow-edit',
  [DatabaseChangeType.SET_DISPLAY_DETAIL]: 'database-change-set-display-detail',
  [DatabaseChangeType.SET_CONTENT_FIELD]: 'database-change-set-content-field',
  [DatabaseChangeType.SET_NEWLINES]: 'database-change-set-newlines',
  [DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE]:
    'database-change-set-backlink-display-type',
  [DatabaseChangeType.SET_DATABASE_ID]: 'database-change-set-database-id',
  [DatabaseChangeType.SET_INCLUDE_VERSION_COMMENT]:
    'database-change-set-include-version-comment',
  [DatabaseChangeType.SET_JIRA_FIELD]: 'database-change-set-jira-field',
  [DatabaseChangeType.SET_JIRA_SERVER_ID]: 'database-change-set-jira-server-id',
  [DatabaseChangeType.SET_CLOUD_ID]: 'database-change-set-cloud-id',
  [DatabaseChangeType.SET_RESOLVE]: 'database-change-set-resolve',
  [DatabaseChangeType.SET_ENTRY_FIELD_FIELD]: 'database-change-set-entry-field-field',
  [DatabaseChangeType.SET_PAGE_SUB_TYPE]: 'database-change-set-page-sub-type',
  [DatabaseChangeType.SET_PARENT_PAGE]: 'database-change-set-parent-page',
  [DatabaseChangeType.SET_PARENT_PAGE_OPTION]: 'database-change-set-parent-page-option',
  [DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE]:
    'database-change-set-is-parent-page-source-database',
  [DatabaseChangeType.SET_PAGE_LOCATION]: 'database-change-set-page-location',
  [DatabaseChangeType.SET_PAGE_TEMPLATE]: 'database-change-set-page-template',
  [DatabaseChangeType.SET_DISPLAY_ENTRY_ON_PAGE]:
    'database-change-set-display-entry-on-page',
  [DatabaseChangeType.SET_FIELD_ID]: 'database-change-set-field-id',
  [DatabaseChangeType.SET_SELECT_OPTIONS]: 'database-change-set-select-options',

  [DatabaseChangeType.SET_FIELD_NAME]: 'database-change-set-field-name',
  [DatabaseChangeType.SET_FIELD_DESCRIPTION]: 'database-change-set-field-description',
  [DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION]:
    'database-change-set-show-field-description',
  [DatabaseChangeType.SET_FIELD_TYPE]: 'database-change-set-field-type',
  [DatabaseChangeType.SET_FIELD_WIDTH]: 'database-change-set-field-width',
  [DatabaseChangeType.REORDER_FIELD]: 'database-change-reorder-field',
  [DatabaseChangeType.CLONE_FIELDS]: 'database-change-clone-fields',
  [DatabaseChangeType.DELETE_FIELD]: 'database-change-delete-field',
  [DatabaseChangeType.SET_VALUE]: 'database-change-set-value',
  [DatabaseChangeType.CREATE_FIELD_AND_SET_VALUE]:
    'database-change-create-field-and-set-value',
  [DatabaseChangeType.BULK_SET_UNDEFINED_VALUES]:
    'database-change-bulk-set-undefined-values',
  [DatabaseChangeType.REORDER_ENTRIES]: 'database-change-reorder-entries',
  [DatabaseChangeType.CLONE_ENTRIES]: 'database-change-clone-entries',
  [DatabaseChangeType.DELETE_ENTRY]: 'database-change-delete-entry',
  [DatabaseChangeType.ADD_SAVED_VIEW]: 'database-change-add-saved-view',
  [DatabaseChangeType.SET_SAVED_VIEW]: 'database-change-set-saved-view',
  [DatabaseChangeType.DELETE_SAVED_VIEW]: 'database-change-delete-saved-view',
  [DatabaseChangeType.REORDER_SAVED_VIEW]: 'database-change-reorder-saved-view',
  [DatabaseChangeType.SET_DEFAULT_SAVED_VIEW]: 'database-change-set-default-saved-view',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE]:
    'database-change-update-saved-view-layout-type',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH]:
    'database-change-update-saved-view-layout-card-width',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID]:
    'database-change-update-saved-view-layout-cover-field-id',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID]:
    'database-change-update-saved-view-layout-group-field-id',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID]:
    'database-change-update-saved-view-layout-date-field-id',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_NAME]: 'database-change-update-saved-view-name',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS]:
    'database-change-update-saved-view-fields',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_SORT]: 'database-change-update-saved-view-sort',
  [DatabaseChangeType.SET_SAVED_VIEW_FILTER]: 'database-change-set-saved-view-filter',
  [DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE]:
    'database-change-set-saved-view-filter-usage-type',
  [DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID]:
    'database-change-set-saved-view-filter-field-id',
  [DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE]:
    'database-change-set-saved-view-filter-type',
  [DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE]:
    'database-change-set-saved-view-filter-value',
  [DatabaseChangeType.APPEND_SAVED_VIEW_FILTER]:
    'database-change-append-saved-view-filter',
  [DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS]:
    'database-change-set-saved-view-calculations',
  [DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION]:
    'database-change-remove-saved-view-calculation',
  [DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER]:
    'database-change-remove-saved-view-filter',
  [DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES]:
    'database-change-bulk-apply-saved-view-changes',
  [DatabaseChangeType.TOGGLE_LOCK_STRUCTURE]: 'database-change-toggle-lock-structure',
  [DatabaseChangeType.CLEAR_ENTRIES]: 'database-change-clear-entries',
  [DatabaseChangeType.SET_DEFAULT_ENABLED]: 'database-change-set-default-enabled',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES]:
    'database-change-update-saved-view-layout-show-numbered-entries',
  [DatabaseChangeType.SET_MENTION_NOTIFICATION_ENABLED]:
    'database-change-set-mention-notification-enabled',
  [DatabaseChangeType.ADD_VALUE_COMMENT_THREAD]:
    'database-change-add-value-comment-thread-id',
  [DatabaseChangeType.REMOVE_VALUE_COMMENT_THREAD]:
    'database-change-remove-value-comment-thread-id',
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD]:
    'database-change-set-value-comment-thread',
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD_RESOLVED]:
    'database-change-set-value-comment-thread-resolved',
  [DatabaseChangeType.SET_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    'database-change-set-value-comment-thread-comment-count',
  [DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    'database-change-increment-value-comment-thread-comment-count',
  [DatabaseChangeType.DECREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT]:
    'database-change-decrement-value-comment-thread-comment-count',
  [DatabaseChangeType.INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER]:
    'database-change-increment-value-comment-thread-version-number',
  [DatabaseChangeType.INSERT_SAVED_VIEW_SORT]: 'database-change-insert-saved-view-sort',
  [DatabaseChangeType.APPEND_SAVED_VIEW_SORT]: 'database-change-append-saved-view-sort',
  [DatabaseChangeType.DELETE_SAVED_VIEW_SORT]: 'database-change-delete-saved-view-sort',
  [DatabaseChangeType.REORDER_SAVED_VIEW_SORT]: 'database-change-reorder-saved-view-sort',
  [DatabaseChangeType.CLEAR_SAVED_VIEW_SORT]: 'database-change-clear-saved-view-sort',
  [DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE]:
    'database-change-update-saved-view-sort-value',
};

export const CollabChangeExperiences: Record<
  DatabaseChangeType,
  UFOExperience | undefined
> = Object.entries(CollabChangeMetrics).reduce((acc, [changeType, metric]) => {
  if (metric) {
    acc[changeType as DatabaseChangeType] = new UFOExperience(metric, {
      type: ExperienceTypes.Operation,
      performanceType: ExperiencePerformanceTypes.InlineResult,
    });
  }
  return acc;
}, {} as Record<DatabaseChangeType, UFOExperience | undefined>);

export const applyTemplateExperience = new UFOExperience('database-apply-template', {
  type: ExperienceTypes.Experience,
  performanceType: ExperiencePerformanceTypes.InlineResult,
});

export const pasteContentExperience = new UFOExperience('database-paste-content', {
  type: ExperienceTypes.Experience,
  performanceType: ExperiencePerformanceTypes.InlineResult,
});
